/**
 * OralDeficiencySignsCard — оралните белези на кръвен дефицит в DHI.
 * Гладък език, ъглов хейлит, афти, парене, бледи венци, кървене —
 * отметките се сверяват с ферритин, B12, фолат и тромбоцити.
 * Данни: GET/POST /api/dental-prevention/oral-signs
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowRight, Droplet, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OralDeficiencySignsCard({ isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const border = isDark ? "border-white/10" : "border-gray-200";

  useEffect(() => {
    axios.get(`${API}/dental-prevention/oral-signs`)
      .then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return null;

  const toggle = async (key) => {
    const next = d.selected.includes(key)
      ? d.selected.filter((k) => k !== key)
      : [...d.selected, key];
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/dental-prevention/oral-signs`, { signs: next });
      setD(data);
    } catch { toast.error("Не се записа"); }
    finally { setSaving(false); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`}
      data-testid="oral-deficiency-signs-card">
      <div className="text-sm font-bold inline-flex items-center gap-1.5">
        <Droplet className="w-4 h-4 text-rose-500" />
        Орални белези на кръвен дефицит
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-60" />}
      </div>
      <p className="text-[11px] opacity-70 mt-0.5">
        Устата показва дефицита преди кръвната картина. Отметнете каквото
        имате — сверяваме го с ферритин, B12, фолат и тромбоцити.
      </p>

      <div className="mt-3 space-y-1.5">
        {d.catalog.map((s) => {
          const on = d.selected.includes(s.key);
          const finding = (d.findings || []).find((f) => f.key === s.key);
          return (
            <button key={s.key} type="button" onClick={() => toggle(s.key)}
              className={`w-full text-left p-2 rounded-lg border transition-colors ${
                on ? (finding?.status === "confirmed"
                  ? "border-rose-500/40 bg-rose-500/10"
                  : "border-emerald-500/40 bg-emerald-500/10")
                  : border}`}
              data-testid={`oral-sign-${s.key}`}>
              <div className="text-[12px] font-semibold inline-flex items-center gap-1.5">
                {on ? <CheckCircle2 className="w-3.5 h-3.5" />
                    : <Circle className="w-3.5 h-3.5 opacity-50" />}
                {s.label_bg}
              </div>
              <div className="text-[10px] opacity-75 mt-0.5 pl-5">{s.why_bg}</div>
              {on && finding?.confirmed_bg && (
                <div className="text-[10px] font-bold text-rose-500 mt-0.5 pl-5"
                  data-testid={`oral-sign-confirmed-${s.key}`}>
                  Потвърдено от лабораториите: {finding.confirmed_bg.join(", ")} е нисък
                </div>
              )}
              {on && finding?.missing_bg && (
                <div className="text-[10px] opacity-70 mt-0.5 pl-5"
                  data-testid={`oral-sign-missing-${s.key}`}>
                  Липсва: {finding.missing_bg.join(", ")}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-[11px] mt-3 opacity-85" data-testid="oral-signs-summary">
        {d.summary_bg}
      </p>
      <Link to="/dashboard/hematology-twin"
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 dark:text-rose-300"
        data-testid="oral-signs-to-hema">
        Отвори хематологичния близнак <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
